// frontend/src/pages/ProductDetailPage.js
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Container,
    Grid,
    Typography,
    Box,
    Button,
    Rating,
    Paper,
    Divider,
    Chip,
    IconButton,
    Dialog,
    DialogContent,
    Breadcrumbs,
    Link,
    Alert,
    CircularProgress
} from '@mui/material';
import { motion } from 'framer-motion';
import CloseIcon from '@mui/icons-material/Close';
import AddShoppingCartIcon from '@mui/icons-material/AddShoppingCart';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import ShareIcon from '@mui/icons-material/Share';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useProducts } from '../context/ProductContext';

// ✅ API URL from environment or production
const API_URL = process.env.REACT_APP_API_URL || 'https://uswellness.co.ke/api';

function ProductDetailPage() {
    const params = useParams();
    const navigate = useNavigate();
    const { products, loading: contextLoading } = useProducts();
    const [selectedImage, setSelectedImage] = useState(0);
    const [quantity, setQuantity] = useState(1);
    const [openDialog, setOpenDialog] = useState(false);
    const [product, setProduct] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const productId = params.id || params.slug;

    useEffect(() => {
        let isMounted = true;
        
        const fetchProduct = async () => {
            if (!productId) {
                setError('Product not found');
                setLoading(false);
                return;
            }

            try {
                setLoading(true);
                setError(null);

                // ✅ FIRST: Try to find in localStorage (faster)
                if (products && products.length > 0) {
                    let foundProduct = null;
                    
                    // Try by ID
                    const idNum = parseInt(productId);
                    foundProduct = products.find(p => p.id === idNum);
                    
                    // Try by slug
                    if (!foundProduct) {
                        foundProduct = products.find(p => p.slug === productId);
                    }
                    
                    // Try by string ID
                    if (!foundProduct) {
                        foundProduct = products.find(p => String(p.id) === productId);
                    }
                    
                    if (foundProduct) {
                        console.log('✅ Product found in localStorage:', foundProduct.title);
                        if (isMounted) {
                            setProduct(foundProduct);
                            setLoading(false);
                            return;
                        }
                    }
                }

                // ✅ SECOND: Try API if not found in localStorage
                console.log('🔍 Fetching product from API:', productId);
                
                let response = await fetch(`${API_URL}/products/${productId}`);
                
                // If not found and we have a slug, try to find by slug through all products
                if (!response.ok && params.slug) {
                    const allProductsRes = await fetch(`${API_URL}/products`);
                    if (allProductsRes.ok) {
                        const allProducts = await allProductsRes.json();
                        const found = allProducts.find(p => p.slug === params.slug);
                        if (found) {
                            response = await fetch(`${API_URL}/products/${found.id}`);
                        }
                    }
                }

                if (!response.ok) {
                    // ✅ FALLBACK: Try to find in localStorage again (in case it was added after initial check)
                    if (products && products.length > 0) {
                        const fallbackProduct = products.find(p => 
                            p.id === parseInt(productId) || 
                            p.slug === productId || 
                            String(p.id) === productId
                        );
                        if (fallbackProduct) {
                            console.log('✅ Fallback: Product found in localStorage:', fallbackProduct.title);
                            if (isMounted) {
                                setProduct(fallbackProduct);
                                setLoading(false);
                                return;
                            }
                        }
                    }
                    throw new Error('Product not found');
                }

                const data = await response.json();
                
                if (isMounted) {
                    console.log('✅ Product fetched from API:', data);
                    setProduct(data);
                    setLoading(false);
                }
            } catch (err) {
                if (isMounted) {
                    console.error('❌ Error fetching product:', err);
                    setError(err.message || 'Product not found');
                    setLoading(false);
                }
            }
        };

        fetchProduct();

        return () => {
            isMounted = false;
        };
    }, [productId, params.slug, products]);

    // Show loading state
    if (contextLoading || loading) {
        return (
            <Container maxWidth="lg" sx={{ py: 8 }}>
                <Box sx={{ 
                    display: 'flex', 
                    justifyContent: 'center', 
                    alignItems: 'center', 
                    minHeight: '50vh', 
                    flexDirection: 'column' 
                }}>
                    <CircularProgress size={60} />
                    <Typography variant="body2" sx={{ mt: 2, color: '#666' }}>
                        Loading product...
                    </Typography>
                </Box>
            </Container>
        );
    }

    if (error || !product) {
        return (
            <Container maxWidth="lg" sx={{ py: 8 }}>
                <Alert severity="error" sx={{ mb: 3 }}>
                    {error || 'Product not found. Please try again.'}
                </Alert>
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                    <Button variant="contained" onClick={() => navigate('/products')}>
                        Back to Products
                    </Button>
                    <Button variant="outlined" onClick={() => {
                        setLoading(true);
                        setError(null);
                        window.location.reload();
                    }}>
                        Retry
                    </Button>
                    <Button variant="text" onClick={() => navigate(-1)}>
                        Go Back
                    </Button>
                </Box>
            </Container>
        );
    }

    const {
        title = product.name || product.title || '',
        description = product.description || '',
        short_description = product.short_description || product.description || '',
        price = product.price || 0,
        discount_price = product.discount_price || null,
        image = product.image || '/placeholder-image.jpg',
        additional_images = product.additional_images || [image],
        category = product.category || '',
        rating = product.rating || 0,
        reviews = product.reviews || 0,
        stock = product.stock || 0,
        brand = product.brand || '',
        benefits = product.benefits || [],
        usage = product.usage || '',
        ingredients = product.ingredients || ''
    } = product;

    const isOnSale = discount_price && discount_price < price;
    const allImages = additional_images.length > 0 ? additional_images : [image];
    const discountPercentage = isOnSale ? Math.round(((price - discount_price) / price) * 100) : 0;

    const handleQuantityChange = (newQuantity) => {
        if (newQuantity >= 1 && newQuantity <= (stock || 999)) {
            setQuantity(newQuantity);
        }
    };

    const handleAddToCart = () => {
        setOpenDialog(true);
    };

    const handleWhatsAppOrder = () => {
        const finalPrice = isOnSale ? discount_price : price;
        const message = `Hello! I would like to order: ${title}\nPrice: KES ${parseFloat(finalPrice).toLocaleString()}\nQuantity: ${quantity}`;
        window.open(`https://wa.me/254714490021?text=${encodeURIComponent(message)}`, '_blank');
    };

    const handleAddToCartConfirm = () => {
        try {
            const cart = JSON.parse(localStorage.getItem('cart') || '[]');
            const existingItem = cart.find(item => item.id === product.id);
            
            if (existingItem) {
                existingItem.quantity = (existingItem.quantity || 0) + quantity;
            } else {
                cart.push({
                    id: product.id,
                    title: title,
                    price: parseFloat(isOnSale ? discount_price : price),
                    originalPrice: parseFloat(price),
                    image: image || '',
                    quantity: quantity,
                    slug: product.slug || product.id
                });
            }
            
            localStorage.setItem('cart', JSON.stringify(cart));
            window.dispatchEvent(new CustomEvent('cartUpdated', { 
                detail: { cart, itemCount: cart.length } 
            }));
            
            setOpenDialog(false);
            setQuantity(1);
            alert(`✅ Added "${title}" to cart!`);
        } catch (error) {
            console.error('Error adding to cart:', error);
            alert('⚠️ Could not add to cart. Please try again.');
        }
    };

    return (
        <Box sx={{ bgcolor: '#f8f9fa', minHeight: '100vh', py: 4 }}>
            <Container maxWidth="lg">
                {/* Breadcrumbs */}
                <Breadcrumbs sx={{ mb: 3 }}>
                    <Link underline="hover" color="inherit" onClick={() => navigate('/')} sx={{ cursor: 'pointer' }}>
                        Home
                    </Link>
                    <Link underline="hover" color="inherit" onClick={() => navigate('/products')} sx={{ cursor: 'pointer' }}>
                        Products
                    </Link>
                    <Typography color="text.primary">{title}</Typography>
                </Breadcrumbs>

                {/* Back Button */}
                <Button
                    startIcon={<ArrowBackIcon />}
                    onClick={() => navigate(-1)}
                    sx={{ mb: 3 }}
                >
                    Back
                </Button>

                {/* ✅ MAIN PRODUCT LAYOUT - Image on Left, Everything on Right */}
                <Grid container spacing={4}>
                    {/* Left Column - Product Images */}
                    <Grid item xs={12} md={6}>
                        <motion.div
                            initial={{ opacity: 0, x: -30 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ duration: 0.6 }}
                        >
                            <Paper
                                elevation={0}
                                sx={{
                                    p: 2,
                                    borderRadius: 3,
                                    bgcolor: 'white',
                                    position: 'relative',
                                    overflow: 'hidden'
                                }}
                            >
                                {isOnSale && (
                                    <Chip
                                        label={`-${discountPercentage}% OFF`}
                                        color="error"
                                        sx={{
                                            position: 'absolute',
                                            top: 16,
                                            right: 16,
                                            zIndex: 10,
                                            fontWeight: 'bold'
                                        }}
                                    />
                                )}
                                <Box
                                    component="img"
                                    src={allImages[selectedImage] || image || '/placeholder-image.jpg'}
                                    alt={title}
                                    sx={{
                                        width: '100%',
                                        height: { xs: 300, md: 450 },
                                        objectFit: 'contain',
                                        borderRadius: 2
                                    }}
                                    onError={(e) => {
                                        e.target.src = '/placeholder-image.jpg';
                                    }}
                                />
                            </Paper>

                            {/* Thumbnail Gallery */}
                            {allImages.length > 1 && (
                                <Box sx={{ display: 'flex', gap: 1, mt: 2, overflowX: 'auto', pb: 1 }}>
                                    {allImages.map((img, index) => (
                                        <Box
                                            key={index}
                                            onClick={() => setSelectedImage(index)}
                                            sx={{
                                                width: 80,
                                                height: 80,
                                                borderRadius: 2,
                                                overflow: 'hidden',
                                                cursor: 'pointer',
                                                border: selectedImage === index ? '3px solid #1976d2' : '1px solid #ddd',
                                                flexShrink: 0,
                                                '&:hover': { borderColor: '#1976d2' }
                                            }}
                                        >
                                            <img
                                                src={img}
                                                alt={`Product view ${index + 1}`}
                                                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                                onError={(e) => {
                                                    e.target.src = '/placeholder-image.jpg';
                                                }}
                                            />
                                        </Box>
                                    ))}
                                </Box>
                            )}
                        </motion.div>
                    </Grid>

                    {/* ✅ Right Column - ALL Product Info including Description */}
                    <Grid item xs={12} md={6}>
                        <motion.div
                            initial={{ opacity: 0, x: 30 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ duration: 0.6 }}
                        >
                            {/* Brand */}
                            {brand && (
                                <Typography variant="caption" sx={{ color: '#666', textTransform: 'uppercase', letterSpacing: 1 }}>
                                    {brand}
                                </Typography>
                            )}

                            {/* Category Chip */}
                            {category && (
                                <Chip
                                    label={category}
                                    size="small"
                                    sx={{ ml: 1, bgcolor: '#1976d215', color: '#1976d2' }}
                                />
                            )}

                            {/* Title */}
                            <Typography variant="h4" component="h1" sx={{ fontWeight: 'bold', mt: 1 }}>
                                {title}
                            </Typography>

                            {/* Rating */}
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 1 }}>
                                <Rating value={rating || 4.5} readOnly precision={0.5} />
                                <Typography variant="body2" color="text.secondary">
                                    ({reviews || 0} reviews)
                                </Typography>
                            </Box>

                            {/* Price */}
                            <Box sx={{ mt: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
                                {isOnSale ? (
                                    <>
                                        <Typography variant="h4" sx={{ color: 'error.main', fontWeight: 'bold' }}>
                                            KES {parseFloat(discount_price).toLocaleString()}
                                        </Typography>
                                        <Typography variant="h6" sx={{ textDecoration: 'line-through', color: 'text.secondary' }}>
                                            KES {parseFloat(price).toLocaleString()}
                                        </Typography>
                                    </>
                                ) : (
                                    <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#1976d2' }}>
                                        KES {parseFloat(price).toLocaleString()}
                                    </Typography>
                                )}
                            </Box>

                            {/* Stock Status */}
                            <Box sx={{ mt: 1 }}>
                                <Typography variant="body2" sx={{ color: stock > 0 ? '#4CAF50' : '#f44336', display: 'flex', alignItems: 'center', gap: 1 }}>
                                    {stock > 0 ? (
                                        <>
                                            <CheckCircleIcon sx={{ fontSize: 18 }} />
                                            ✓ In Stock ({stock} available)
                                        </>
                                    ) : (
                                        '✕ Out of Stock'
                                    )}
                                </Typography>
                            </Box>

                            {/* ✅ FULL DESCRIPTION - Now on the right side */}
                            <Box sx={{ mt: 3 }}>
                                <Typography variant="body1" sx={{ color: '#555', lineHeight: 1.8 }}>
                                    {description || short_description}
                                </Typography>
                            </Box>

                            {/* Benefits */}
                            {benefits && benefits.length > 0 && (
                                <Box sx={{ mt: 3 }}>
                                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                                        Benefits:
                                    </Typography>
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                        {benefits.map((benefit, index) => (
                                            <Chip
                                                key={index}
                                                label={benefit}
                                                size="small"
                                                sx={{ bgcolor: '#e8f5e9', color: '#2e7d32' }}
                                            />
                                        ))}
                                    </Box>
                                </Box>
                            )}

                            {/* Quantity */}
                            {stock > 0 && (
                                <Box sx={{ mt: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
                                    <Typography variant="body2">Quantity:</Typography>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Button
                                            variant="outlined"
                                            size="small"
                                            onClick={() => handleQuantityChange(quantity - 1)}
                                            disabled={quantity <= 1}
                                            sx={{ minWidth: 36 }}
                                        >
                                            -
                                        </Button>
                                        <Typography variant="body1" sx={{ width: 40, textAlign: 'center' }}>
                                            {quantity}
                                        </Typography>
                                        <Button
                                            variant="outlined"
                                            size="small"
                                            onClick={() => handleQuantityChange(quantity + 1)}
                                            disabled={quantity >= stock}
                                            sx={{ minWidth: 36 }}
                                        >
                                            +
                                        </Button>
                                    </Box>
                                </Box>
                            )}

                            {/* Action Buttons */}
                            <Box sx={{ mt: 4, display: 'flex', flexDirection: 'column', gap: 2 }}>
                                <Button
                                    variant="contained"
                                    size="large"
                                    startIcon={<AddShoppingCartIcon />}
                                    onClick={handleAddToCart}
                                    disabled={stock === 0}
                                    fullWidth
                                    sx={{
                                        borderRadius: 2,
                                        py: 1.5,
                                        fontWeight: 'bold'
                                    }}
                                >
                                    {stock > 0 ? 'Add to Cart' : 'Out of Stock'}
                                </Button>

                                <Button
                                    variant="contained"
                                    size="large"
                                    startIcon={<WhatsAppIcon />}
                                    onClick={handleWhatsAppOrder}
                                    disabled={stock === 0}
                                    fullWidth
                                    sx={{
                                        bgcolor: '#25D366',
                                        '&:hover': { bgcolor: '#1DA851' },
                                        borderRadius: 2,
                                        py: 1.5,
                                        fontWeight: 'bold'
                                    }}
                                >
                                    Order via WhatsApp
                                </Button>

                                <Box sx={{ display: 'flex', gap: 2 }}>
                                    <Button
                                        variant="outlined"
                                        startIcon={<FavoriteBorderIcon />}
                                        fullWidth
                                        sx={{ borderRadius: 2, py: 1.5 }}
                                    >
                                        Add to Wishlist
                                    </Button>
                                    <Button
                                        variant="outlined"
                                        startIcon={<ShareIcon />}
                                        fullWidth
                                        sx={{ borderRadius: 2, py: 1.5 }}
                                    >
                                        Share
                                    </Button>
                                </Box>
                            </Box>

                            {/* ✅ PRODUCT DETAILS (Usage, Ingredients) - Also on right */}
                            <Box sx={{ mt: 4 }}>
                                <Paper sx={{ p: 3, borderRadius: 3, bgcolor: '#f8f9fa' }}>
                                    <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <span>📋</span> Product Details
                                    </Typography>
                                    <Divider sx={{ mb: 3 }} />

                                    {usage && (
                                        <Box sx={{ mb: 3 }}>
                                            <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#333' }}>
                                                How to Use:
                                            </Typography>
                                            <Typography variant="body2" sx={{ color: '#555', mt: 1 }}>
                                                {usage}
                                            </Typography>
                                        </Box>
                                    )}

                                    {ingredients && (
                                        <Box sx={{ mb: 3 }}>
                                            <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#333' }}>
                                                Ingredients:
                                            </Typography>
                                            <Typography variant="body2" sx={{ color: '#555', mt: 1 }}>
                                                {ingredients}
                                            </Typography>
                                        </Box>
                                    )}

                                    <Box>
                                        <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#333' }}>
                                            Description:
                                        </Typography>
                                        <Typography variant="body2" sx={{ color: '#555', mt: 1 }}>
                                            {description || short_description}
                                        </Typography>
                                    </Box>
                                </Paper>
                            </Box>
                        </motion.div>
                    </Grid>
                </Grid>
            </Container>

            {/* Add to Cart Dialog */}
            <Dialog open={openDialog} onClose={() => setOpenDialog(false)}>
                <DialogContent sx={{ p: 4, textAlign: 'center', minWidth: 300 }}>
                    <Box sx={{ position: 'relative' }}>
                        <IconButton
                            onClick={() => setOpenDialog(false)}
                            sx={{ position: 'absolute', top: -10, right: -10 }}
                        >
                            <CloseIcon />
                        </IconButton>
                        <Box sx={{ fontSize: 60, mb: 2 }}>🛒</Box>
                        <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                            Add to Cart?
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#666', mt: 1 }}>
                            {title} - Quantity: {quantity}
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#1976d2', mt: 0.5, fontWeight: 'bold' }}>
                            Total: KES {parseFloat((isOnSale ? discount_price : price) * quantity).toLocaleString()}
                        </Typography>
                        <Box sx={{ mt: 3, display: 'flex', flexDirection: 'column', gap: 1 }}>
                            <Button
                                variant="contained"
                                onClick={handleAddToCartConfirm}
                                startIcon={<AddShoppingCartIcon />}
                            >
                                Confirm Add to Cart
                            </Button>
                            <Button
                                variant="outlined"
                                onClick={() => setOpenDialog(false)}
                            >
                                Continue Shopping
                            </Button>
                            <Button
                                variant="contained"
                                onClick={() => {
                                    handleAddToCartConfirm();
                                    setTimeout(handleWhatsAppOrder, 500);
                                }}
                                startIcon={<WhatsAppIcon />}
                                sx={{ bgcolor: '#25D366', '&:hover': { bgcolor: '#1DA851' } }}
                            >
                                Add & Order via WhatsApp
                            </Button>
                        </Box>
                    </Box>
                </DialogContent>
            </Dialog>
        </Box>
    );
}

export default ProductDetailPage;